/** Public origin. Each page's canonical is this origin plus that page's own path. */
const ORIGIN = 'https://hesychia.ai';
const OG_IMAGE = `${ORIGIN}/og-image.png`;

/** Real routes from App.tsx. `/` is the shared index.html; other paths get their own file at build. */
const PAGE_PATHS = [
  '/',
  '/pricing/',
  '/privacy/',
  '/changelog/',
  '/terms_of_service/',
  '/legal/terms-of-service',
  '/account-deletion/',
  '/signin/',
  '/signup/',
  '/auth/callback/',
  '/billing/success',
  '/billing/cancel',
  '/password_reset/',
  '/password_reset_confirmation/',
  '/set_password/',
  '/account/',
  '/document_storage',
  '/feedback/',
  '/analytics/',
];

/**
 * The one headline already on that page. Static HTML must use this text;
 * the React page renders the same words in its single h1.
 * @type {Record<string, string>}
 */
const PAGE_H1 = {
  '/': 'Hesychia is a calm AI assistant.',
  '/pricing/': 'Pricing',
  '/privacy/': 'Privacy',
  '/changelog/': 'Changelog',
  '/terms_of_service/': 'Terms of Service',
  '/legal/terms-of-service': 'Terms of Service',
  '/account-deletion/': 'Hesychia — Request account deletion',
  '/signin/': 'Hesychia.ai',
  '/signup/': 'Sign Up',
  '/auth/callback/': 'Sign In',
  '/billing/success': 'Payment received',
  '/billing/cancel': 'Checkout canceled',
  '/password_reset/': 'Reset Password',
  '/password_reset_confirmation/': 'Check Your Email',
  '/set_password/': 'Hesychia',
  '/account/': 'Account — Hesychia',
  '/document_storage': 'Documents — Hesychia',
  '/feedback/': 'Feedback — Hesychia',
  '/analytics/': 'Analytics — Hesychia',
};

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function canonicalForPath(pathname) {
  if (!PAGE_PATHS.includes(pathname)) return null;
  return `${ORIGIN}${pathname}`;
}

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function h1ForPath(pathname) {
  return PAGE_H1[pathname] || null;
}

/**
 * Unique document titles, 30–60 characters as rendered. Main term first.
 * Too short or too long throws. Nothing is sliced to fit.
 * @type {Record<string, string>}
 */
const PAGE_TITLES = {
  '/': 'Hesychia — quiet, focused AI chat',
  '/pricing/': 'Hesychia pricing — $10/month, no ads',
  '/privacy/': 'Hesychia privacy — stored, never sold',
  '/changelog/': 'Hesychia changelog — dated product updates',
  '/terms_of_service/': 'Hesychia terms of service — binding terms',
  '/legal/terms-of-service': 'Hesychia legal terms — terms of service',
  '/account-deletion/': 'Hesychia account deletion — soft-delete',
  '/signin/': 'Sign in to Hesychia — quiet, focused chat',
  '/signup/': 'Sign up for Hesychia — $10/month, no ads',
  '/auth/callback/': 'Hesychia sign-in — returning you to chat',
  '/billing/success': 'Hesychia payment received — subscription on',
  '/billing/cancel': 'Hesychia checkout canceled — no charge made',
  '/password_reset/': 'Reset your Hesychia password — email link',
  '/password_reset_confirmation/': 'Hesychia password reset — check your email',
  '/set_password/': 'Set a new Hesychia password — account access',
  '/account/': 'Hesychia account — profile and subscription',
  '/document_storage': 'Hesychia documents — stored and searchable',
  '/feedback/': 'Hesychia feedback — tell us what to change',
  '/analytics/': 'Hesychia analytics — usage for your account',
};

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function titleForPath(pathname) {
  const title = PAGE_TITLES[pathname];
  if (!title) return null;
  if (title.length < 30 || title.length > 60) {
    throw new Error(
      `title for ${pathname} is ${title.length} characters (want 30-60), left uncut: ${title}`,
    );
  }
  return title;
}

/**
 * What the reader gets on that page, 110–160 characters. No silent trim.
 * @type {Record<string, string>}
 */
const PAGE_DESCRIPTIONS = {
  '/': 'Hesychia is a $10/month AI chat product from AI ML Operations, LLC. It stores your documents so answers can use your files.',
  '/pricing/': '$10/month, flat. No ads, no upsells, one price. One public plan includes AI chat, your documents, and business accounts for your team.',
  '/privacy/': 'Hesychia stores your account, conversations, and uploaded documents so answers can use your files. It does not sell your information or use advertising cookies.',
  '/changelog/': 'Dated notes of shipped Hesychia changes: conversation memory, account deletion, document upload, and subscriptions you can upgrade, change, or cancel.',
  '/terms_of_service/': 'These Terms of Service are a legally binding agreement between you and AI ML Operations, LLC governing access to and use of the Hesychia chat service.',
  '/legal/terms-of-service': 'Public copy of the Hesychia Terms of Service, last updated July 27, 2026. The terms govern use of the chat service operated by AI ML Operations, LLC.',
  '/account-deletion/': 'Steps to request deletion of a Hesychia account without signing in. Deletion is a soft-delete: profile, conversations, and documents may remain.',
  '/signin/': 'Sign in to an existing Hesychia account with email and password, or use the SSO buttons when registration is open. New visitors can create an account.',
  '/signup/': 'Create a Hesychia account, then complete payment on Stripe. The public price is $10/month with no ads and no upsells. Sign in if you already have an account.',
  '/auth/callback/': 'Hesychia is finishing sign-in and sending you back to the app. If the provider returns an error, this page shows it and links back to the sign-in form.',
  '/billing/success': 'Your Hesychia subscription checkout completed and no further payment step is required here. Sign in with the account you just created to continue into the app.',
  '/billing/cancel': 'No charge was made. Your Hesychia account may already exist. Sign in and open billing again, or return to sign up to retry Stripe Checkout.',
  '/password_reset/': 'Enter the email on your Hesychia account and we will send a link to set a new password. The link opens a page where you choose that password.',
  '/password_reset_confirmation/': 'If an account exists for that address, Hesychia sent a link to set a new password. Check the inbox for that email, then return to sign in.',
  '/set_password/': 'Choose a new password for your Hesychia account using the link from the reset email. After it is saved, sign in with that password.',
  '/account/': 'View the Hesychia account profile and subscription. Upgrade, change, or cancel the plan, and company managers can invite or update team accounts.',
  '/document_storage': 'Upload documents to Hesychia and search them from chat. Personal and company libraries stay separate. Files remain until you delete them or delete the account.',
  '/feedback/': 'Submit feedback about Hesychia and read feedback you already sent. The form records the note you write. It does not change your subscription or documents.',
  '/analytics/': 'See Hesychia usage for your account: prompt volume, conversation volume, team seat activity, and response times. Company managers can open company insights.',
};

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function descriptionForPath(pathname) {
  const description = PAGE_DESCRIPTIONS[pathname];
  if (!description) return null;
  if (description.length < 110 || description.length > 160) {
    throw new Error(
      `description for ${pathname} is ${description.length} characters (want 110-160), left uncut: ${description}`,
    );
  }
  return description;
}

module.exports = {
  ORIGIN,
  OG_IMAGE,
  PAGE_PATHS,
  PAGE_H1,
  PAGE_TITLES,
  PAGE_DESCRIPTIONS,
  canonicalForPath,
  h1ForPath,
  titleForPath,
  descriptionForPath,
};
